// HSSNA 认证客户端
// 规格: SPEC §4.1 API 客户端架构 / §7 安全合规 / §4.3 MFA
// 契约: api-contracts/openapi/s1-identity-openapi.yaml
//
// 职责:
//   - 调用 S1 端点 (login/refresh/logout/mfa)
//   - JWT 注入 (Authorization: Bearer <access_token>)
//   - Idempotency-Key (UUID v4, §3.5)
//   - 过期前 60s 自动 refresh (willExpireWithin(payload, 60))
//   - Refresh rotation: refresh 后用新 refresh_token 替换旧的
//
// 约束 (SPEC §4.1):
//   - X-Family-Group-Id 由网关重写,前端禁发送
//   - JWT 不在前端验证签名 (仅解码 payload 判断过期)

import type { StorageAdapter } from './token-store.js';
import { TokenStore, webStorageAdapter } from './token-store.js';
import { decodeJwt, isExpired, willExpireWithin } from './jwt.js';

/** AuthClient 创建选项 */
export interface AuthClientOptions {
  /** S1 Identity 服务基础 URL (如 https://api.dev.hssna.cn) */
  readonly baseUrl: string;
  /** 自定义 TokenStore (默认使用 webStorageAdapter) */
  readonly tokenStore?: TokenStore;
  /** 自定义存储适配器 (用于创建默认 TokenStore) */
  readonly storageAdapter?: StorageAdapter;
  /** 自定义 fetch 实现 (测试/SSR 注入) */
  readonly fetchImpl?: typeof fetch;
  /** 过期前提前 refresh 秒数 (默认 60s) */
  readonly refreshSkewSeconds?: number;
}

/** login 响应 (snake_case, 含 MFA 状态) */
export interface LoginResponse {
  readonly access_token?: string;
  readonly refresh_token?: string;
  readonly mfa_required: boolean;
  readonly mfa_completed: boolean;
  readonly session_id?: string;
  readonly role?: string;
  readonly roles?: readonly string[];
}

/** refresh 响应 (snake_case, 对齐后端 S1 RefreshResponse) */
export interface RefreshResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly expires_in: number;
  readonly token_type: string;
}

/** mfa challenge 响应 */
export interface MfaChallengeResponse {
  readonly challenge_id: string;
  readonly expires_at: string;
}

/** mfa verify 响应 */
export interface MfaVerifyResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly mfa_completed: boolean;
}

/** 请求选项 */
interface RequestOptions {
  readonly auth?: boolean;
  readonly idempotent?: boolean;
}

/** 默认过期前 refresh 提前量 (秒) */
const DEFAULT_REFRESH_SKEW_SECONDS = 60;

/**
 * S1 Identity 认证客户端
 *
 * @example
 * const client = new AuthClient({ baseUrl: 'https://api.dev.hssna.cn' });
 * const res = await client.login('user', 'pass');
 * if (!res.mfa_completed) {
 *   const ch = await client.mfaChallenge('totp', res.session_id!);
 *   await client.mfaVerify(ch.challenge_id, '123456');
 * }
 * // 后续请求自动注入 JWT, 过期前 60s 自动 refresh
 * const token = await client.getValidAccessToken();
 */
export class AuthClient {
  private readonly baseUrl: string;
  private readonly tokenStore: TokenStore;
  private readonly fetchImpl: typeof fetch;
  private readonly refreshSkew: number;
  private refreshPromise: Promise<string> | null = null;

  constructor(options: AuthClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    // ★ 绑定 fetch 到 globalThis, 避免 "Illegal invocation" 错误
    // (globalThis.fetch 赋值给实例字段后调用, this 上下文不再是 window, 浏览器拒绝执行)
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.refreshSkew = options.refreshSkewSeconds ?? DEFAULT_REFRESH_SKEW_SECONDS;
    this.tokenStore =
      options.tokenStore ??
      new TokenStore(options.storageAdapter ?? webStorageAdapter);
  }

  /**
   * 用户登录
   * POST /v1/auth/login
   * - 非 MFA 用户: 返回 access_token + refresh_token + mfa_completed=true
   * - MFA 用户: 返回 mfa_required=true + session_id (不含令牌)
   * 成功后自动存储 access_token / refresh_token (非 MFA 场景)
   */
  async login(username: string, password: string): Promise<LoginResponse> {
    const body = JSON.stringify({ username, password });
    const data = (await this.request('/v1/auth/login', 'POST', body)) as LoginResponse;
    // MFA 未启用: 存储令牌
    if (data.access_token && data.refresh_token) {
      await this.tokenStore.setTokens(data.access_token, data.refresh_token);
    }
    return data;
  }

  /**
   * 刷新 Token (refresh rotation)
   * POST /v1/auth/refresh
   * 使用 refresh_token 换取新 access_token + refresh_token, 旧的 refresh_token 作废
   *
   * @param refreshToken 显式传入 refresh_token; 不传则从 TokenStore 读取
   */
  async refresh(refreshToken?: string): Promise<RefreshResponse> {
    const token = refreshToken ?? (await this.tokenStore.getRefreshToken());
    if (!token) {
      throw new AuthError(401, 'No refresh token available');
    }
    const body = JSON.stringify({ refresh_token: token });
    const data = (await this.request('/v1/auth/refresh', 'POST', body, {
      idempotent: true,
    })) as RefreshResponse;
    if (data.access_token && data.refresh_token) {
      // Refresh rotation: 新 refresh_token 替换旧的
      await this.tokenStore.setTokens(data.access_token, data.refresh_token);
    }
    return data;
  }

  /**
   * 登出
   * POST /v1/auth/logout
   * 无论服务端是否成功,均清除本地 token
   */
  async logout(): Promise<void> {
    try {
      await this.request('/v1/auth/logout', 'POST', null, {
        auth: true,
        idempotent: true,
      });
    } finally {
      await this.tokenStore.clear();
      this.refreshPromise = null;
    }
  }

  /**
   * 发起 MFA 挑战
   * POST /v1/auth/mfa/challenge
   */
  async mfaChallenge(mfaType: string, sessionId: string): Promise<MfaChallengeResponse> {
    const body = JSON.stringify({ mfa_type: mfaType, session_id: sessionId });
    return (await this.request('/v1/auth/mfa/challenge', 'POST', body, {
      idempotent: true,
    })) as MfaChallengeResponse;
  }

  /**
   * 验证 MFA
   * POST /v1/auth/mfa/verify
   * 验证通过后自动存储新签发的 access_token / refresh_token
   */
  async mfaVerify(challengeId: string, code: string): Promise<MfaVerifyResponse> {
    const body = JSON.stringify({ challenge_id: challengeId, code });
    const data = (await this.request('/v1/auth/mfa/verify', 'POST', body, {
      idempotent: true,
    })) as MfaVerifyResponse;
    if (data.access_token && data.refresh_token) {
      await this.tokenStore.setTokens(data.access_token, data.refresh_token);
    }
    return data;
  }

  /**
   * 获取有效 access_token
   * - token 未过期且不会在 refreshSkew 内过期 → 直接返回
   * - token 已过期或即将过期 → 自动 refresh (并发去重)
   *
   * @returns 有效的 access_token;无 token 或 refresh 失败时返回 null
   */
  async getValidAccessToken(): Promise<string | null> {
    const token = await this.tokenStore.getAccessToken();
    if (!token) {
      return null;
    }
    const payload = decodeJwt(token);
    if (!payload) {
      // 无法解码的 token 直接返回 (交由服务端拒绝)
      return token;
    }
    if (isExpired(payload) || willExpireWithin(payload, this.refreshSkew)) {
      return await this.autoRefresh();
    }
    return token;
  }

  /** 获取底层 TokenStore (供调用方查询/清除) */
  getTokenStore(): TokenStore {
    return this.tokenStore;
  }

  /**
   * 敏感操作密码验证
   * POST /v1/auth/verify-password (需 Authorization + Idempotency-Key)
   *
   * @param password 用户输入的登录密码
   * @returns verify_token (短期授权令牌, 5min TTL, 用于 X-Verify-Token 头)
   *
   * @example
   * ```ts
   * const { verify_token } = await authClient.verifyPassword(userPassword);
   * await apiFetch('/v1/patients/123', {
   *   method: 'DELETE',
   *   headers: { 'X-Verify-Token': verify_token },
   * });
   * ```
   */
  async verifyPassword(password: string): Promise<{ verify_token: string }> {
    const body = JSON.stringify({ password });
    return (await this.request('/v1/auth/verify-password', 'POST', body, {
      auth: true,
      idempotent: true,
    })) as { verify_token: string };
  }

  /**
   * 敏感操作 MFA 验证
   * POST /v1/auth/mfa/verify (需 Authorization + Idempotency-Key)
   *
   * 与登录流程的 mfaVerify 不同, 此方法用于已登录用户执行敏感操作时的二次验证,
   * 返回 verify_token 而非新的 access_token.
   *
   * @param code TOTP 验证码或短信验证码
   * @param method 验证方式 ('mfa-totp' | 'mfa-sms')
   * @returns verify_token (短期授权令牌)
   */
  async verifyMfaForSensitive(
    code: string,
    method: 'mfa-totp' | 'mfa-sms',
  ): Promise<{ verify_token: string }> {
    const body = JSON.stringify({ code, mfa_type: method });
    return (await this.request('/v1/auth/mfa/verify', 'POST', body, {
      auth: true,
      idempotent: true,
    })) as { verify_token: string };
  }

  /** 自动 refresh,并发请求去重 (同一时刻只发一个 refresh) */
  private async autoRefresh(): Promise<string> {
    if (this.refreshPromise) {
      return await this.refreshPromise;
    }
    this.refreshPromise = this.refresh().then((res) => res.access_token);
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * 统一请求封装
   * - 注入 X-Request-Id (必填, §3.5)
   * - 注入 Idempotency-Key (幂等端点, §3.5)
   * - 注入 Authorization Bearer (需认证端点)
   * - 解包 ApiResponse { data, meta } → data
   */
  private async request(
    path: string,
    method: string,
    body: string | null,
    options: RequestOptions = {},
  ): Promise<unknown> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Request-Id': generateRequestId(),
    };

    if (options.idempotent) {
      headers['Idempotency-Key'] = generateIdempotencyKey();
    }

    if (options.auth) {
      const accessToken = await this.tokenStore.getAccessToken();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      method,
      headers,
      body,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new AuthError(response.status, text);
    }

    if (response.status === 204) {
      return null;
    }

    const json = (await response.json()) as { data?: unknown };
    // HSSNA ApiResponse 封装: { data, meta }; 退化兼容直接返回
    return json.data ?? json;
  }
}

/** 认证错误 */
export class AuthError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, body: string) {
    super(`Auth request failed: ${status}`);
    this.name = 'AuthError';
    this.status = status;
    this.body = body;
  }
}

/** 生成 X-Request-Id */
function generateRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** 生成 Idempotency-Key (优先 crypto.randomUUID) */
function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return generateRequestId();
}
